import { CloseOutlineIcon, InfoOutlineIcon } from '@carboncopyco/icons';
import type { DeviceType } from '@carboncopyco/types';
import {
  Button,
  ColorHex,
  type InputState,
  InputText,
  type Placement,
  Tooltip,
  TooltipMessage,
} from '@carboncopyco/ui';
import { getMaxZIndex, makeDraggable } from '@carboncopyco/utils';
import {
  type FocusEvent,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import DimensionCard from './DimensionCard';
import { MenuFloating } from './MenuFloating';
import { CONFIG } from './constants';

const isDimensionValid = (val: string | number, maxValue: number) => {
  return val !== '' && Number(val) >= 360 && Number(val) <= maxValue;
};

export type DimensionPanelProps = {
  visible: boolean;
  offset: [number, number];
  onClose: () => void;
  containerRef: RefObject<HTMLElement>;
  onDimensionChange: (dimension) => void;
  onSubmit: (dimension) => void;
  placement?: Placement;
  width: number;
  height: number;
  initialType?: DeviceType;
  title?: string;
};

const config = CONFIG;

const maxWidth = config.desktop.dimension.width;
const maxHeight = config.desktop.dimension.height;

/**
 * @author Vladimir Antonic, Stefan Varga
 */
export const DimensionPanel = ({
  visible,
  offset,
  onClose,
  containerRef,
  onDimensionChange,
  onSubmit,
  width,
  height,
  placement = 'top-end',
  initialType = 'desktop',
  title = 'Screen size',
}: DimensionPanelProps) => {
  const onRefLoad = (el: HTMLDivElement) => {
    el && makeDraggable(el, { handle: '.handle-dragging' });
  };
  const timeoutRef = useRef(null);
  const infoRef = useRef(null);
  const widthInputRef = useRef<HTMLInputElement>(null);
  const heightInputRef = useRef<HTMLInputElement>(null);
  const errorRefWidth = useRef(null);
  const errorRefHeight = useRef(null);
  const [selectedType, setSelectedType] = useState(initialType);
  const [dimension, setDimension] = useState<{
    width: string | number;
    height: string | number;
  }>({ width, height });

  const [validDimension, setValidDimension] = useState({
    width,
    height,
  });

  const [responsiveDimenstions, setResponsiveDimentions] = useState({
    width,
    height,
  });

  useEffect(() => {
    function handleResize() {
      const containerRect = containerRef.current.getBoundingClientRect();

      setResponsiveDimentions({
        width: containerRect.width,
        height: containerRect.height,
      });

      if (selectedType === 'responsive') {
        setDimension({
          width: containerRect.width,
          height: containerRect.height,
        });
      }
    }
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [selectedType]);

  const handleChangeWidth = (e: FocusEvent<HTMLInputElement>) => {
    setSelectedType('custom');
    const newWidth = Number.parseInt(e.target.value as string) || '';

    setDimension(({ height: prevHeight }) => ({
      width: newWidth,
      height: prevHeight,
    }));
  };

  const handleChangeHeight = (e: FocusEvent<HTMLInputElement>) => {
    setSelectedType('custom');
    const newHeight = Number.parseInt(e.target.value) || '';

    setDimension(({ width: prevWidth }) => ({
      width: prevWidth,
      height: newHeight,
    }));
  };

  const validHeightResponsiveDimension = useMemo(() => {
    return responsiveDimenstions.height > maxHeight
      ? responsiveDimenstions.height
      : maxHeight;
  }, [responsiveDimenstions.height]);

  const validWidthResponsiveDimension = useMemo(() => {
    return responsiveDimenstions.width > maxWidth
      ? responsiveDimenstions.width
      : maxWidth;
  }, [responsiveDimenstions.width]);

  const changeDimensionOnBlur = () => {
    const isWidthValid = isDimensionValid(
      dimension.width,
      validWidthResponsiveDimension
    );
    const isHeightValid = isDimensionValid(
      dimension.height,
      validHeightResponsiveDimension
    );

    let newDimension = {
      width: validDimension.width,
      height: validDimension.height,
    };

    if (isWidthValid && isHeightValid) {
      newDimension = {
        width: Number(dimension.width),
        height: Number(dimension.height),
      };
    }

    const type = getTypeByDimension(newDimension);

    setSelectedType(type || 'custom');

    setDimension(newDimension);
    setValidDimension(newDimension);
    onDimensionChange({ ...newDimension, type });
  };

  const validate = (
    value: number | string,
    maxValue: number
  ): {
    state: InputState;
    value?: 'min' | 'max';
  } => {
    if (value === '') {
      return {
        state: 'normal',
      };
    }

    if (Number.isNaN(Number(value))) {
      return {
        state: 'error',
      };
    }

    if (Number(value) < 360) {
      return {
        state: 'error',
        value: 'min',
      };
    }

    if (Number(value) > maxValue) {
      return {
        state: 'error',
        value: 'max',
      };
    }

    return {
      state: 'normal',
    };
  };

  const getTypeByDimension = ({
    width: givenWidth,
    height: givenHeight,
  }): DeviceType => {
    if (
      responsiveDimenstions.width === givenWidth &&
      responsiveDimenstions.height === givenHeight
    ) {
      return 'responsive';
    }

    const matchedDevice = (Object.keys(config) as DeviceType[]).find(
      (device) => {
        return (
          config[device].dimension?.width === givenWidth &&
          config[device].dimension?.height === givenHeight
        );
      }
    );

    return matchedDevice || 'custom';
  };

  const handleDimensionChange = (type: DeviceType) => {
    if (config[type].dimension) {
      setDimension(config[type].dimension);

      onDimensionChange({
        width: config[type].dimension.width,
        height: config[type].dimension.height,
        type,
      });
      setSelectedType(type);
      return;
    }

    if (type === 'responsive') {
      setDimension({
        width: responsiveDimenstions.width,
        height: responsiveDimenstions.height,
      });

      onDimensionChange({
        width: responsiveDimenstions.width,
        height: responsiveDimenstions.height,
        type,
      });
    }

    setSelectedType(type);

    if (type === 'custom') {
      setDimension((prev) => ({
        width: '',
        height: prev.height,
      }));
      widthInputRef.current.focus();
    }
  };

  const isApplyDisabled = useMemo(() => {
    return (
      !isDimensionValid(dimension.width, validWidthResponsiveDimension) ||
      !isDimensionValid(dimension.height, validHeightResponsiveDimension) ||
      (width === dimension.width && height === dimension.height)
    );
  }, [
    dimension.width,
    dimension.height,
    validWidthResponsiveDimension,
    validHeightResponsiveDimension,
    width,
    height,
  ]);

  const widthState = useMemo(() => {
    return validate(dimension.width, validWidthResponsiveDimension);
  }, [dimension.width, validWidthResponsiveDimension]);

  const heightState = useMemo(() => {
    return validate(dimension.height, validHeightResponsiveDimension);
  }, [dimension.height, validHeightResponsiveDimension]);

  return (
    <MenuFloating
      title={title}
      placement={placement}
      visible={visible}
      offset={offset}
      onClose={onClose}
      isDraggable
      anchorRef={containerRef}
      dragRef={onRefLoad}
    >
      <div className="flex flex-col items-center justify-center">
        <div className="flex items-center justify-center mb-6">
          <InputText
            ref={widthInputRef}
            tabIndex={0}
            className="h-ds-lg-input"
            state={widthState.state}
            name="width"
            maxLength={4}
            onChange={handleChangeWidth}
            onBlur={() => {
              timeoutRef.current = setTimeout(() => {
                changeDimensionOnBlur();
              }, 100);
            }}
            onKeyPress={(keypress) => {
              if (keypress.key === 'Enter') {
                changeDimensionOnBlur();
              }
            }}
            onFocus={() => {
              widthInputRef.current.select();
            }}
            rightElement={
              widthState.state === 'error' ? (
                <div
                  ref={errorRefWidth}
                  style={{ zIndex: getMaxZIndex() }}
                >
                  <InfoOutlineIcon color={ColorHex.danger400} />
                </div>
              ) : null
            }
            value={`${Math.round(Number(dimension.width)) || ''}`}
          />
          {widthState.state === 'error' && (
            <Tooltip
              shadow="md"
              noArrow
              placement="top"
              anchorRef={errorRefWidth}
              interactiveBorder={0}
              fixedWidth={true}
            >
              <div className="w-48">
                <TooltipMessage
                  titleColor="text-ds-natural-300"
                  title={
                    widthState.value === 'min'
                      ? 'Minimal value is 360px'
                      : 'Value is bigger than your screen size. Use a different value or adjust your screen.'
                  }
                />
              </div>
            </Tooltip>
          )}
          <div className="mx-ds-25 mx-1">
            <CloseOutlineIcon size={16} />
          </div>
          <InputText
            ref={heightInputRef}
            tabIndex={1}
            className="h-ds-lg-input"
            state={heightState.state}
            name="height"
            maxLength={4}
            onFocus={() => {
              heightInputRef.current.select();
            }}
            onBlur={() =>
              (timeoutRef.current = setTimeout(() => {
                changeDimensionOnBlur();
              }, 100))
            }
            onKeyPress={(keypress) => {
              if (keypress.key === 'Enter') {
                changeDimensionOnBlur();
              }
            }}
            onChange={handleChangeHeight}
            value={`${Math.round(Number(dimension.height)) || ''}`}
            rightElement={
              heightState.state === 'error' ? (
                <div
                  ref={errorRefHeight}
                  style={{ zIndex: getMaxZIndex() }}
                >
                  <InfoOutlineIcon color={ColorHex.danger400} />
                </div>
              ) : null
            }
          />
          {heightState.state === 'error' && (
            <Tooltip
              shadow="md"
              noArrow
              placement="top"
              anchorRef={errorRefHeight}
              interactiveBorder={0}
              fixedWidth={true}
            >
              <div className="w-48">
                <TooltipMessage
                  titleColor="text-ds-natural-300"
                  title={
                    heightState.value === 'min'
                      ? 'Minimal value is 360px'
                      : 'Value is bigger than your screen size. Use a different value or adjust your screen.'
                  }
                />
              </div>
            </Tooltip>
          )}
        </div>
        <div
          className="flex flex-col justify-center items-center w-full"
          ref={infoRef}
        >
          {Object.keys(config).map((key: DeviceType) => {
            const {
              label,
              imageComponent,
              dimension: configDimension,
              hint,
            } = config[key];

            return (
              <DimensionCard
                key={key}
                label={label}
                imageComponent={imageComponent}
                onSelect={() => {
                  clearTimeout(timeoutRef.current);
                  handleDimensionChange(key);
                }}
                resolution={configDimension}
                hint={hint}
                isSelected={selectedType === key}
              />
            );
          })}
        </div>
      </div>
      <div className="mt-3">
        <Button
          label="Apply"
          style={{ width: '222px' }}
          disabled={isApplyDisabled}
          onClick={() => {
            onSubmit({ ...dimension, type: selectedType });
          }}
        />
      </div>
    </MenuFloating>
  );
};
